<template>
  <div>
    <!-- When extracting layers in separate component, key for same layer component should always
     be present (to force l-tile-layer rerender) as when updating attribution in l-tile-layer
     leaflet actually updates l-map component and cannot find parent if it is not direct child of l-map
     because for attribution vue wrapper uses this.$parent.mapObject.attributionControl -->
    <l-tile-layer
      v-if="mapType.type === 'osm'"
      key="osm"
      :url="mapType.url"
      :subdomains="mapType.subdomains"
      :attribution="mapType.attribution"
      :options="tileOptions"
    />
    <l-tile-layer-google
      v-if="mapType.type === 'google'"
      :key="mapType.key"
      :apikey="mapType.apiKey"
      :options="tileOptions"
      lang="hr"
      region="HR"
    />
    <l-tile-layer
      v-if="mapType.type === 'here'"
      :key="mapType.key"
      :url="mapType.url"
      :attribution="mapType.attribution"
      :options="tileOptions"
    />
    <l-tile-layer-maplibre
      v-if="mapType.type === 'vector' && !webglUnsupported"
      :key="mapType.key"
      :style-url="mapType.style"
      :attribution="mapType.attribution"
      :options="tileOptions"
      @unsupported="onVectorUnsupported"
    />
    <!--    Vector tiles need a WebGL context. Without one MapLibre draws nothing at all, so the
            raster source the type names stands in - otherwise the markers would sit over an empty
            grey plane with only a console message to explain it. Same data behind both, so the
            type's own attribution still holds.-->
    <l-tile-layer
      v-if="showRasterFallback"
      key="vector-raster-fallback"
      :url="mapType.fallback_url"
      :attribution="mapType.attribution"
      :options="fallbackTileOptions"
    />
  </div>
</template>

<script>

import { LTileLayer } from 'vue2-leaflet'
import LTileLayerGoogle from '@/global/components/map/Vue2LeafletGoogleMutant'
import LTileLayerMaplibre from '@/global/components/map/Vue2LeafletMaplibre'
import { createNamespacedHelpers } from 'vuex'
import { tileZoomSetup } from '@/global/common/mapConfig'
import { notify } from '@/global/services/notification/notificationBus'
import { loadMaplibre, hasWebglSupport } from '@/global/components/map/maplibreLoader'

const { mapGetters, mapActions } = createNamespacedHelpers('map-type')

export default {
  name: 'MapTileLayers',
  components: {
    LTileLayer,
    LTileLayerGoogle,
    LTileLayerMaplibre
  },

  data () {
    return {
      // Set once the vector layer reports it cannot start. It is the browser that cannot, not this
      // particular style, so it stays set for every vector type from then on.
      webglUnsupported: false
    }
  },

  computed: {
    ...mapGetters({
      mapType: 'getMapType',
      tileOptions: 'getTileOptions'
    }),

    // The type names what to draw in its place; a type that names nothing gets nothing rather
    // than a raster source picked for it here.
    showRasterFallback () {
      return this.mapType.type === 'vector' && this.webglUnsupported && !!this.mapType.fallback_url
    },

    /**
     * The vector branch of the watcher below leaves out `maxNativeZoom` on purpose - vector tiles
     * overzoom client side. Raster tiles do not, so the fallback has to clamp again or the last
     * zoom levels come out blank.
     */
    fallbackTileOptions () {
      return {
        ...this.tileOptions,
        ...tileZoomSetup.raster
      }
    }
  },

  watch: {
    mapType: {
      deep: true,
      handler (mapType) {
        if (mapType?.type === 'google') {
          // Read off the type, the way the vector branch below reads its own: Google's
          // imagery does not stop at the same level for every one of them - satellite
          // goes closer than the road tiles do. A type that says nothing keeps what
          // every Google type used to get.
          this.setTileOptions({
            maxZoom: mapType?.maxZoom || tileZoomSetup.google.maxZoom,
            maxNativeZoom: mapType?.maxNativeZoom || tileZoomSetup.google.maxNativeZoom,
            type: mapType?.google_map_type || 'roadmap',
            layers: mapType?.layers ? mapType.layers : []
          })
        }
        else if (mapType?.type === 'vector') {
          // Vector tiles are rendered client side, so they overzoom cleanly past the source's
          // native max zoom - no maxNativeZoom clamping needed.
          this.setTileOptions({
            maxZoom: mapType?.maxZoom || tileZoomSetup.vector.maxZoom
          })
        }
        else {
          this.setTileOptions({
            ...tileZoomSetup.raster
          })
        }
      }
    }
  },

  mounted () {
    // The vector layer in the template cannot mount until `mapType` arrives, and `mapType` comes from a view
    // config request (see live-tracking store's fetch). So the whole cost of MapLibre - fetching
    // ~1.1MB of bundle and, more to the point, evaluating it on the main thread - used to start
    // only once that round trip had already finished, and the first tile could not be requested
    // until it was done. webpackPrefetch covers the fetching; nothing covered the evaluating.
    //
    // Warming the same promise here runs all of it alongside the config request instead of after
    // it, so by the time a vector type is named the module is ready and only the style fetch is
    // left. Fire and forget: the layer awaits this promise itself, and a failure to load is the
    // layer's to report, not this hook's.
    //
    // Not gated on the map type, because the whole point is to run before it is known. It is gated
    // on WebGL, since evaluating a GL bundle a browser cannot use would be pure main-thread cost.
    if (hasWebglSupport()) {
      loadMaplibre()
    }
  },

  methods: {
    ...mapActions(['setMapType', 'setTileOptions']),

    onVectorUnsupported () {
      if (this.webglUnsupported) {
        return
      }

      this.webglUnsupported = true
      notify(this.$t('base.vector_map_not_supported'), { type: 'warning' })
    }
  }
}
</script>
